import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { isEmpty } from 'lodash-es'

import { TREATMENT_OPTIONS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { S4C_PROGRAM_OPTION_FLOW } from '@lyrahealth-inc/shared-app-logic/src/features/memberPreferences/constants'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { useGenerateSearchRequestPayload } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/hooks/useGenerateSearchRequestPayload'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { AppContext } from '@lyrahealth-inc/ui-core-crossplatform/src/context'

import { useAppDispatch } from '../../data/storeConfiguration/store'
import {
  getId,
  getIsUserInternational,
  getIsUserLoggedIn,
  getUserAge,
  getUserCountryCode,
} from '../../data/user/userSelectors'
import { getOnboardSelectedCareLanguage } from '../../features/onboard/data/onboardSelectors'
import { clearOnsiteProviders, getOnsiteProviders } from '../../features/onboard/data/t1-flow/t1-flow-actions'
import { getT1OnsiteProvidersAddresses } from '../../features/onboard/data/t1-flow/t1Selectors'

export const useFetchSpeculativeOnsiteProviders = ({ shouldClearOnsiteProviders = true } = {}) => {
  const { areExpandedGlobalPreferencesEnabled } = useContext(AppContext)
  const { showProviderSearchOption } = useFlags()

  const dispatch = useAppDispatch()
  const selectedCareLanguage = useSelector(getOnboardSelectedCareLanguage)
  const ageInteger = useSelector(getUserAge)
  const isInternationalUser = useSelector(getIsUserInternational)
  const onsiteProviderAddresses = useSelector(getT1OnsiteProvidersAddresses)?.toJS()
  const userCountryIsoCode = useSelector(getUserCountryCode)
  const userId = useSelector(getId)
  const isLoggedIn = useSelector(getIsUserLoggedIn)

  const searchRequest = useGenerateSearchRequestPayload({
    locationSearchData: null,
    isUserInternational: isInternationalUser,
    meetingFormat: MEETING_FORMATS.IN_PERSON,
    treatmentOption: TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
    selectedCareLanguage,
    matchFor: SEARCHER_TYPE_TO_MATCH_FOR.SELF,
    isValidMemberPreferencesTreatmentOption: true,
    searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW.THERAPY,
    ageInteger,
    shouldShowAvailabilityPreference: false,
    lyraId: userId,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  })

  useEffect(() => {
    if (!userCountryIsoCode || !shouldClearOnsiteProviders) {
      return
    }

    dispatch(clearOnsiteProviders())
  }, [dispatch, shouldClearOnsiteProviders, userCountryIsoCode])

  useEffect(() => {
    if (!showProviderSearchOption || isEmpty(searchRequest) || onsiteProviderAddresses?.length > 0 || !isLoggedIn) {
      return
    }
    dispatch(
      getOnsiteProviders({
        lyraId: userId,
        data: { ...searchRequest, treatmentPreference: TREATMENT_OPTIONS.INDIVIDUAL_THERAPY },
      }),
    )
  }, [showProviderSearchOption, userId, searchRequest, onsiteProviderAddresses?.length, dispatch, isLoggedIn])
}
